import React, { useCallback, useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useSelector, useDispatch } from 'react-redux'
import { StoreSelectionPage } from './index'
import { AtCoreServiceClient } from '@api/AtCoreServiceClient'
import { ensureStoreRegionKnown } from '@api/userAndStoreContext'
import { EnvironmentDetail } from '@amzn/ihm-associate-tools-js-lib'
import { getStoreId, getSelectedStoreId, getStoreScanStatus } from '@atredux/selectors/storeSelector'
import { setSelectedStoreId } from '@atredux/slices/storeSlice'
import { savePreviousTool } from '@atredux/slices/toolSlice'
import { RootState } from '@atredux/store'
import { _navigateToTool } from '@components/SideBar/utils/SideBarUtil'
import { getAssociateEntryTool } from '@services/ToolMenuConfigService'
import { SET_STORE_ERROR, SWITCH_STORE_ERROR } from './constants'

/** Outcome of a completed selection, passed to `onComplete`. */
export interface IStoreSelectionResult {
    /**
     * False when the associate re-picked the store whose scan-in is already active: nothing about
     * the scan-in state changed, so callers should not send them to Associate Entry.
     */
    needsScanIn: boolean
}

interface IStoreSelectionPageContainerProps {
    onComplete: (result?: IStoreSelectionResult) => void
    showSwitchButton?: boolean
    onSwitchStore?: (storeId: string) => void
    isSheetOpen?: boolean
    onClose?: () => void
}

/**
 * StoreSelectionPageContainer
 *
 * Container component that integrates StoreSelectionPage with the application's
 * Redux state, routing, and store service.
 */
export const StoreSelectionPageContainer: React.FC<IStoreSelectionPageContainerProps> = ({
  onComplete,
  showSwitchButton,
  onSwitchStore,
  isSheetOpen,
  onClose,
}) => {
    const dispatch = useDispatch()
    const [selectionError, setSelectionError] = useState<string | null>(null)

    const currentStoreId = useSelector(getStoreId)
    const selectedStoreId = useSelector(getSelectedStoreId)
    const isCurrentScanned = useSelector((state: RootState) => currentStoreId ? getStoreScanStatus(state, currentStoreId) : false)
    const [userId, setUserId] = useState<string>('default-user')

    useEffect(() => {
        const fetchUserId = async () => {
            const storeId = EnvironmentDetail.getStoreId()
            const alias = await AtCoreServiceClient.getInstance().getUserAlias(storeId)
            setUserId(alias)
        }
        fetchUserId()
    }, [])

    const handleStoreSelected = useCallback(async (storeId: string) => {
        try {
            setSelectionError(null)
            console.log(`JWOStoreManagementPlatform: Store selected: ${storeId}`)

            // Before the dispatch, because onComplete navigates straight to a tool and the iframe URL
            // has to carry the store's own cell, not the one we signed in against.
            await ensureStoreRegionKnown(storeId)
            dispatch(setSelectedStoreId(storeId))
            console.log('JWOStoreManagementPlatform: Selected store ID set in Redux')

            if (onComplete) {
                onComplete()
                return
            }
        } catch (error) {
            console.error('JWOStoreManagementPlatform: Error setting store:', error)
            setSelectionError(SET_STORE_ERROR)
        }
    }, [dispatch, onComplete])

    const handleRetrySelection = useCallback(() => {
        setSelectionError(null)
    }, [])

    const handleSwitchStore = useCallback(async (storeId: string) => {
        try {
            setSelectionError(null)
            console.log(`JWOStoreManagementPlatform: Switching to store: ${storeId}`)

            // User selected their current (scanned) store — reset selectedStoreId
            if (storeId === currentStoreId) {
                dispatch(setSelectedStoreId(undefined))
                if (onComplete) {
                    // An active scan-in for this store means no new scan is required.
                    onComplete({ needsScanIn: !isCurrentScanned })
                }
                return
            }

            // Same ordering requirement as handleStoreSelected: whichever branch runs below can put a
            // tool on screen immediately, and its URL is only correct once the store's cell is known.
            await ensureStoreRegionKnown(storeId)

            if (onSwitchStore) {
                onSwitchStore(storeId)
            } else {
                const tool = getAssociateEntryTool()
                if (tool) {
                    dispatch(savePreviousTool())
                    dispatch(setSelectedStoreId(storeId))
                    _navigateToTool(tool)
                }
            }

            if (onComplete) {
                onComplete({ needsScanIn: true })
            }
        } catch (error) {
            console.error('JWOStoreManagementPlatform: Error switching store:', error)
            console.error('JWOStoreManagementPlatform: Failed to switch from', currentStoreId, 'to', storeId)

            setSelectionError(SWITCH_STORE_ERROR)
        }
    }, [onComplete, onSwitchStore, currentStoreId, isCurrentScanned, dispatch])

    return (
    <StoreSelectionPage
      userId={userId}
      onStoreSelected={handleStoreSelected}
      selectionError={selectionError}
      onRetrySelection={handleRetrySelection}
      currentStoreId={isCurrentScanned ? currentStoreId : undefined}
      selectedStoreId={selectedStoreId}
      showSwitchButton={showSwitchButton}
      onSwitchStore={handleSwitchStore}
      isSheetOpen={isSheetOpen}
      onClose={onClose}
    />
    )
}

export default StoreSelectionPageContainer
